import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQueryClient } from 'react-query';
import {
  ContactPanelMockRouter,
  CallIntelInsightPanelMockRouter,
  CallIntelTasksMockRouter,
  CallIntelTssMockRouter,
  CallIntelFeedbackMockRouter,
  CallIntelPmsSyncMockRouter,
  CallIntelDemoInterceptorsContext,
} from '@frontend/api-call-intel';
import { DEMO_PRESET_PERSONS, MockPersons } from '@frontend/api-person';
import { DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockInterceptorEntry, RequestInterceptorEntry, ResponseInterceptorEntry, useFetch } from '@frontend/fetch';
import { useAppScopeStore } from '@frontend/scope';

const INTERCEPTOR_ID_PREFIX = 'call-intel-demo-panel';

const INTERCEPTOR_ROUTERS: FeatureMockRouter<CallIntelDemoInterceptorsContext>[] = [
  ContactPanelMockRouter,
  CallIntelInsightPanelMockRouter,
  CallIntelTasksMockRouter,
  CallIntelTssMockRouter,
  CallIntelFeedbackMockRouter,
  CallIntelPmsSyncMockRouter,
];

export interface UseCallInsightDemoPanelInterceptorProps {
  enabled: boolean;
  locationId: string;
}

/**
 * Installs the demo interceptors needed to show a call insight panel for the preset demo person (Jane Doe)
 * in demo surfaces (such as the Call Intelligence trial teaser or other demo entry points).
 *
 * - Includes `ContactPanelMockRouter` so `RecentCalls` for the demo person is rewritten into a demo request.
 * - The vertical is derived from the given location so the response matches the location.
 * - Ready state is local to the hook, so unmounting it never flips the ready flag of another demo surface.
 */
export const useCallInsightDemoPanelInterceptor = ({
  enabled,
  locationId,
}: UseCallInsightDemoPanelInterceptorProps) => {
  const {
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    removeMockInterceptor,
    removeRequestInterceptor,
    removeResponseInterceptor,
  } = useFetch();
  const { accessibleLocationData, getScopeName } = useAppScopeStore();
  const queryClient = useQueryClient();

  const [isDemoDataReady, setIsDemoDataReady] = useState(false);
  const interceptorsInitializedRef = useRef(false);
  const requestInterceptors = useRef<RequestInterceptorEntry[]>([]);
  const responseInterceptors = useRef<ResponseInterceptorEntry[]>([]);
  const mockInterceptor = useRef<MockInterceptorEntry | null>(null);

  const vertical = accessibleLocationData?.[locationId]?.vertical ?? Vertical.DENTAL;

  const context = useMemo<CallIntelDemoInterceptorsContext>(
    () => ({
      vertical,
      locationId,
      mockPersonsInstance: new MockPersons({ persons: [DEMO_PRESET_PERSONS['jane-doe']] }),
      interceptorIdPrefix: INTERCEPTOR_ID_PREFIX,
      getScopeName,
      hasVoiceAiAccess: false,
    }),
    [vertical, locationId, getScopeName]
  );

  const invalidateQueries = useCallback(() => {
    INTERCEPTOR_ROUTERS.forEach((router) => router.invalidateQueries?.(queryClient));
  }, [queryClient]);

  const cleanup = useCallback(() => {
    if (!interceptorsInitializedRef.current) return;
    mockInterceptor.current?.id && removeMockInterceptor(mockInterceptor.current.id);
    requestInterceptors.current.forEach(({ id }) => removeRequestInterceptor(id));
    responseInterceptors.current.forEach(({ id }) => removeResponseInterceptor(id));
    requestInterceptors.current = [];
    responseInterceptors.current = [];
    mockInterceptor.current = null;
    interceptorsInitializedRef.current = false;
    setIsDemoDataReady(false);
    invalidateQueries();
  }, [invalidateQueries, removeMockInterceptor, removeRequestInterceptor, removeResponseInterceptor]);

  useEffect(() => {
    if (!enabled || !locationId) return;

    requestInterceptors.current = DemoInterceptorUtils.getRequestInterceptors(INTERCEPTOR_ROUTERS, context);
    responseInterceptors.current = DemoInterceptorUtils.getResponseInterceptors(INTERCEPTOR_ROUTERS, context);
    mockInterceptor.current = DemoInterceptorUtils.getMockRouter(INTERCEPTOR_ROUTERS, context, INTERCEPTOR_ID_PREFIX);

    addMockInterceptor(mockInterceptor.current);
    requestInterceptors.current.forEach(addRequestInterceptor);
    responseInterceptors.current.forEach(addResponseInterceptor);
    interceptorsInitializedRef.current = true;
    invalidateQueries();
    setIsDemoDataReady(true);

    return cleanup;
  }, [
    enabled,
    locationId,
    context,
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    invalidateQueries,
    cleanup,
  ]);

  return {
    isDemoDataReady: enabled && isDemoDataReady,
    vertical,
    demoPerson: DEMO_PRESET_PERSONS['jane-doe'],
  };
};
